import React from "react";
import { Box, Typography, Button, Stack, Link } from "@mui/joy";
import { useNavigate, useParams } from "react-router-dom";
import MarkdownRenderer from "../../common/components/MarkdownRenderer";

interface Position {
  id: string;
  displayNameOverride: string;
  slugOverride: string;
  description: string;
  isPartTime: boolean;
  isFullTime: boolean;
  isHourly: boolean;
  isSalary: boolean;
  minHourlyCents: number | null;
  maxHourlyCents: number | null;
  minSalaryCents: number | null;
  maxSalaryCents: number | null;
  receivesTips: boolean;
}

interface PositionsListCardProps {
  positions: Position[];
  companyName: string;
  companyAbout: string;
  isEmployerView?: boolean;
}

const PositionsListCard: React.FC<PositionsListCardProps> = ({ 
  positions, 
  companyName, 
  companyAbout, 
  isEmployerView = false
}) => {
  const navigate = useNavigate();
  const { company } = useParams();

  const getEmploymentType = (isPartTime: boolean, isFullTime: boolean) => {
    if (isPartTime && isFullTime) {
      return 'Full-time or Part-time';
    } else if (isFullTime) {
      return 'Full-time';
    } else if (isPartTime) {
      return 'Part-time';
    }
    return 'Employment type not specified';
  };

  const getWageType = (isHourly: boolean, isSalary: boolean) => {
    const types = [];
    
    if (isHourly) types.push('Hourly');
    if (isSalary) types.push('Salary');
    
    return types.length > 0 ? types.join(' • ') : 'Wage type not specified';
  };

  const formatWage = (isHourly: boolean, isSalary: boolean, minHourlyCents: number | null, maxHourlyCents: number | null, minSalaryCents: number | null, maxSalaryCents: number | null, receivesTips: boolean) => {
    const formatCents = (cents: number) => {
      return `$${(cents / 100).toFixed(2)}`;
    };
    
    let wageDisplay = '';
    
    if (isHourly && (minHourlyCents || maxHourlyCents)) {
      const suffix = '/hr';
      if (minHourlyCents && maxHourlyCents) {
        wageDisplay = `${formatCents(minHourlyCents)} to ${formatCents(maxHourlyCents)}${suffix}`;
      } else if (minHourlyCents) {
        wageDisplay = `${formatCents(minHourlyCents)}+${suffix}`;
      } else if (maxHourlyCents) {
        wageDisplay = `Up to ${formatCents(maxHourlyCents)}${suffix}`;
      }
    }
    
    if (isSalary && (minSalaryCents || maxSalaryCents)) {
      const suffix = '/yr';
      if (minSalaryCents && maxSalaryCents) {
        wageDisplay = `${formatCents(minSalaryCents)} to ${formatCents(maxSalaryCents)}${suffix}`;
      } else if (minSalaryCents) {
        wageDisplay = `${formatCents(minSalaryCents)}+${suffix}`;
      } else if (maxSalaryCents) {
        wageDisplay = `Up to ${formatCents(maxSalaryCents)}${suffix}`;
      }
    }
    
    // Add tips to the wage display if applicable
    if (receivesTips) {
      if (wageDisplay) {
        wageDisplay += ' + Tips';
      } else {
        wageDisplay = 'Tips';
      }
    }
    
    return wageDisplay || null;
  };

  return (
    <Box
      sx={{
        bgcolor: "white",
        borderRadius: "16px",
        p: { xs: 4, md: 4 },
        boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
        maxWidth: { xs: "600px", md: "none" },
      }}
    >
      <Stack spacing={3}>
        {/* Positions List */}
        <Box>
          <Typography
            level="h2"
            sx={{
              fontSize: { xs: "1.75rem", md: "2.25rem" },
              fontWeight: 600,
              color: "#1A1B1D",
              mb: 3,
            }}
          >
            Open Positions
          </Typography>

          <Stack spacing={2}>
            {positions.map((position) => {
              const employmentType = getEmploymentType(position.isPartTime, position.isFullTime);
              const wageType = getWageType(position.isHourly, position.isSalary);
              const wageDisplay = formatWage(
                position.isHourly, 
                position.isSalary, 
                position.minHourlyCents, 
                position.maxHourlyCents, 
                position.minSalaryCents, 
                position.maxSalaryCents,
                position.receivesTips
              );

              return (
                <Box
                  key={position.id}
                  onClick={() => navigate(`/apply/${company}/${position.slugOverride}`)}
                  sx={{
                    p: 3,
                    borderRadius: "12px",
                    backgroundColor: "#F8F9FA",
                    border: "1px solid #E5E7EB",
                    transition: "all 0.2s ease-in-out",
                    cursor: "pointer",
                    "&:hover": {
                      borderColor: "#2e2d7e",
                      backgroundColor: "#F0F2F5",
                    },
                  }}
                >
                  <Stack spacing={1}>
                    {/* Position Title */}
                    <Typography
                      level="h3"
                      sx={{
                        fontSize: "1.25rem",
                        fontWeight: 600,
                        color: "#2e2d7e",
                      }}
                    >
                      {position.displayNameOverride}
                    </Typography>

                    {/* Job Information */}
                    <Typography
                      level="body-md"
                      sx={{
                        fontSize: "0.875rem",
                        color: "#7B7F85",
                        fontWeight: 500,
                      }}
                    >
                      {employmentType} • {wageType} {wageDisplay && `• ${wageDisplay}`}
                    </Typography>
                  </Stack>
                </Box>
              );
            })}
          </Stack>
        </Box>

        {/* Company Information */}
        <Box>
          <Typography
            level="h3"
            sx={{
              fontSize: { xs: "1.5rem", md: "1.875rem" },
              fontWeight: 600,
              color: "#1A1B1D",
              mb: 2,
            }}
          >
            About {companyName}
          </Typography>
          
          <MarkdownRenderer 
            content={companyAbout}
            sx={{
              fontSize: { xs: "1rem", md: "1.125rem" },
            }}
          />
        </Box>
      </Stack>
    </Box>
  );
};

export default PositionsListCard; 